/**
 * The destination somebody asked for before they were asked to sign in.
 *
 * Deep links into this portal are the normal case, not the edge one - an
 * operator pastes an order URL into a message during an incident - and since the
 * launcher moved in here (`/pilot`) a typed URL is also the only way to reach
 * the screen that opens the other three portals. Landing on the dashboard after
 * signing in loses that: the operator has to remember what they were opening and
 * type it again, on a portal they only reached because they had typed it once.
 *
 * Two rules, and both matter. The path is remembered at the moment the gate
 * decides to show a sign-in, because an OAuth round trip through Google leaves
 * and comes back on whatever URL the identity provider was told - by then the
 * original path is gone from the address bar. And it is TAKEN rather than read:
 * one sign-in restores one destination, so a later manual visit to the dashboard
 * is not silently redirected somewhere the operator did not ask for.
 *
 * `sessionStorage`, not `localStorage`: this belongs to one tab's one sign-in.
 */

const KEY = 'cib.internal.returnTo';

/** The dashboard is where an unqualified sign-in lands anyway - nothing to store. */
function worthRemembering(path: string): boolean {
  return path !== '' && path !== '/' && path.startsWith('/') && !path.startsWith('//');
}

export function rememberDestination(path: string): void {
  try {
    if (worthRemembering(path)) globalThis.sessionStorage?.setItem(KEY, path);
  } catch {
    // A browser refusing storage still has to render a sign-in.
  }
}

/**
 * The remembered destination, once. Null when there is none, when it is the
 * dashboard anyway, or when storage is unavailable.
 *
 * The `//` refusal is not decoration: the value round-trips through storage, and
 * `//evil.example.com` is a protocol-relative URL that a router would happily
 * treat as a path and a browser would treat as another origin.
 */
export function takeDestination(): string | null {
  try {
    const stored = globalThis.sessionStorage?.getItem(KEY) ?? null;
    globalThis.sessionStorage?.removeItem(KEY);
    return stored !== null && worthRemembering(stored) ? stored : null;
  } catch {
    return null;
  }
}
